import { TrinketLine } from '@/domain/models/TrinketLine'
import { Point } from '@/domain/models/Point'
import { State, Tools } from '@/store/state'
import { TrinketV1 } from '@/domain/models/TrinketV1'
import { Orientation } from '@/domain/models/TrinketSettings'

export const isValidColor = (color: string): boolean => {
    return /^#[0-9A-F]{6}$/i.test(color)
}

export const loadFile = (files: FileList | null): Promise<ProgressEvent> => {
    return new Promise((resolve) => {
        if (files && files.length > 0) {
            const fileReader = new FileReader()
            const file = files[0]

            if (file) {
                fileReader.onload = resolve
                fileReader.readAsText(file)
            }
        }
    })
}

export function calculatePixelsFromPosition (point: number, gridMeasure: number): number {
    return point * gridMeasure + gridMeasure / 2
}

export const calculateDistanceBetweenPointAndSegment = (point: Point, line: TrinketLine): number => {
    const distance2 = quadraticDistance({
        x: line.x,
        y: line.y2
    }, {
        x: line.x2,
        y: line.y2
    })

    if (distance2 === 0) {
        return quadraticDistance(point, {
            x: line.x,
            y: line.y
        })
    }

    let t = ((point.x - line.x) * (line.x2 - line.x) + (point.y - line.y) * (line.y2 - line.y)) / distance2
    t = Math.max(0, Math.min(1, t))

    return Math.sqrt(quadraticDistance(point, {
        x: line.x + t * (line.x2 - line.x),
        y: line.y + t * (line.y2 - line.y)
    }))
}

export const quadraticDistance = (vector1: Point, vector2: Point): number => Math.pow(vector1.x - vector2.x, 2) + Math.pow(vector1.y - vector2.y, 2)

export const migrateFromV1 = (v1: TrinketV1): State | null => {
    if (!v1.configuration || !v1.points || !v1.texts || !v1.lines) {
        return null
    }

    return {
        configuration: {
            line: {
                continueLine: false
            },
            point: {
                margin: v1.configuration.point.margin,
                radius: v1.configuration.point.radius
            },
            saving: {
                saveOnExit: v1.configuration.saving.saveOnExit,
                autoSave: {
                    timeInterval: v1.configuration.saving.autosave.timeInterval,
                    active: v1.configuration.saving.autosave.active
                }
            },
            grid: {
                width: v1.configuration.grid.width,
                orientation: v1.configuration.grid.orientation === 'LANDSCAPE' ? Orientation.LANDSCAPE : Orientation.PORTRAIT,
                showGridHelper: v1.configuration.grid.showGridHelper,
                gridHelperColor: v1.configuration.grid.gridHelperColor,
                height: v1.configuration.grid.height,
                showGridAxis: true,
                gridAxisColor: 'hsl(0, 79%, 54%)'
            }
        },
        palette: [],
        saveInterval: undefined,
        trinketGrid: {
            texts: v1.texts.map(t => ({
                x: t.x,
                y: t.y,
                color: '#212121',
                text: t.text,
                uuid: t.uuid,
                fontSize: 16
            })),
            colors: {
                fill: v1.colors.fill.hex,
                stroke: v1.colors.stroke.hex,
                lineStroke: v1.colors.lineStroke.hex
            },
            currentLine: undefined,
            lines: v1.lines.map(l => ({
                x: l.x1,
                y: l.y1,
                x2: l.x2,
                y2: l.y2,
                uuid: l.uuid,
                stroke: l.stroke
            })),
            points: v1.points.map(p => ({
                x: p.x,
                y: p.y,
                uuid: p.uuid,
                stroke: p.stroke,
                fill: p.fill
            })),
            selectedTool: Tools.NONE
        },
        settingsModal: {
            show: false
        },
        toast: undefined,
        version: process.env.PACKAGE_VERSION
    }
}
